import { Breadcrumbs, Button, Chip, Skeleton, Stack } from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";

import { AdvancedAcquisitionReport } from "./advanced-acquisition-report";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { BreadcrumbChip } from "../../components";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { REPORT_STATUS } from "../../constants";
import { formatDateISO } from "../../utils/formatTime";
import { useMission } from "./useMission";
import useSWR from "swr";
import { useSnackbar } from "../../hooks";

export function AcquisitionReportLayout() {
    const { missionUuid } = useParams();
    const [searchParams] = useSearchParams();
    const { openSnackbar } = useSnackbar();

    const experimentMissionUuid = searchParams.get("experimentMissionUuid");

    const {
        data: experimentMission,
        error: experimentMissionFetchError,
        isLoading: experimentMissionIsLoading,
    } = useSWR(
        experimentMissionUuid
            ? `${BACKEND_ROUTES.EXPERIMENT_MISSION}/${experimentMissionUuid}`
            : null
    );

    const {
        data: mission,
        error: missionFetchError,
        isLoading: missionLoading,
    } = useMission(missionUuid);

    const mergedFetchError = missionFetchError || experimentMissionFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    const handleCopyUploadSessionId = () => {
        const uploadSessionId = mission?.metadata?.uploadSession?.id;
        if (uploadSessionId) {
            navigator.clipboard.writeText(uploadSessionId);
            openSnackbar("Upload Session ID copied", "success");
        }
    };

    return (
        <>
            {experimentMissionIsLoading || missionLoading ? (
                <Skeleton width="50%" height="5em" />
            ) : (
                <>
                    <Breadcrumbs separator="/" sx={{ mb: 1 }}>
                        <BreadcrumbChip
                            label={mission.Site.Contract.Company.name}
                        />
                        <BreadcrumbChip label={mission.Site.Contract.name} />
                        <BreadcrumbChip label={mission.Site.name} />
                        <BreadcrumbChip label={formatDateISO(mission.date)} />
                    </Breadcrumbs>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Chip
                            label={
                                mission.reportStatus ??
                                "No report status available"
                            }
                            color={
                                reportStatusColors[mission.reportStatus] ||
                                "default"
                            }
                        />
                        {mission?.metadata?.uploadSession?.id && (
                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ContentCopyIcon fontSize="small" />}
                                onClick={handleCopyUploadSessionId}
                            >
                                Upload Session ID
                            </Button>
                        )}
                    </Stack>
                </>
            )}
            <AdvancedAcquisitionReport experimentMission={experimentMission} />
        </>
    );
}

const reportStatusColors = {
    [REPORT_STATUS.VALIDATED]: "success",
    [REPORT_STATUS.REJECTED]: "error",
    [REPORT_STATUS.WARNING]: "warning",
};
